/**
 * Piezas compartidas de las vistas de reportes de inventario (RES-47): tarjetas KPI, nota de
 * valorización, tabla ordenable y tooltip de los gráficos.
 */
import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { formatCurrency } from '../../utils/formatCurrency';
import { money, sortRows } from '../../utils/inventoryReports';

/**
 * Nota de valorización de un importe: parte con costo estimado y movimientos sin costo.
 * @param {{amount: Object, t: Function}} props - Importe `{ value, estimatedValue, uncosted }`
 * @returns {JSX.Element|null} Nota
 */
export function MoneyNote({ amount, t }) {
  const { estimatedValue, uncosted } = money(amount);
  if (!estimatedValue && !uncosted) return null;
  return (
    <span className="report-money-note">
      {estimatedValue !== 0 && (
        <span data-note="estimated">
          {t('inventory.reports.notes.estimated', { amount: formatCurrency(estimatedValue) })}
        </span>
      )}
      {uncosted > 0 && (
        <span data-note="uncosted">
          {t('inventory.reports.notes.uncosted', { count: uncosted })}
        </span>
      )}
    </span>
  );
}

/**
 * Fila de tarjetas KPI.
 * @param {{tiles: Array<{key: string, label: string, value: string, sub?: React.ReactNode, tone?: string}>}} props - Tarjetas
 * @returns {JSX.Element} KPIs
 */
export function KpiTiles({ tiles }) {
  return (
    <div className="report-kpis">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={`report-kpi${tile.tone ? ` report-kpi--${tile.tone}` : ''}`}
          data-kpi={tile.key}
        >
          <span className="report-kpi-label">{tile.label}</span>
          <span className="report-kpi-value">{tile.value}</span>
          {tile.sub && <span className="report-kpi-sub">{tile.sub}</span>}
        </div>
      ))}
    </div>
  );
}

/**
 * Tabla con encabezados ordenables.
 * @param {Object} props - Props
 * @param {Array<{key: string, label: string, sortValue?: Function, render: Function, align?: string, cellClass?: Function}>} props.columns - Columnas
 * @param {Object[]} props.rows - Filas
 * @param {Function} props.rowKey - Clave de la fila
 * @param {{key: string, direction: string}} [props.defaultSort] - Orden inicial
 * @param {string} [props.caption] - Título accesible
 * @param {string} [props.testId] - `data-testid`
 * @param {Function} props.t - `t`
 * @returns {JSX.Element} Tabla
 */
export function SortableTable({ columns, rows, rowKey, defaultSort, caption, testId, t }) {
  const [sort, setSort] = useState(defaultSort || null);
  const sorted = useMemo(() => {
    const column = sort && columns.find((c) => c.key === sort.key);
    if (!column || !column.sortValue) return rows;
    return sortRows(rows, column.sortValue, sort.direction);
  }, [rows, columns, sort]);

  const toggle = (column) => {
    if (!column.sortValue) return;
    setSort((prev) =>
      prev && prev.key === column.key
        ? { key: column.key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
        : { key: column.key, direction: column.align === 'right' ? 'desc' : 'asc' }
    );
  };

  return (
    <div className="report-table-wrap">
      <table className="report-table" data-testid={testId}>
        {caption && <caption className="report-sr-only">{caption}</caption>}
        <thead>
          <tr>
            {columns.map((column) => {
              const active = sort && sort.key === column.key;
              const ariaSort = active
                ? sort.direction === 'asc'
                  ? 'ascending'
                  : 'descending'
                : 'none';
              return (
                <th
                  key={column.key}
                  scope="col"
                  className={column.align === 'right' ? 'is-num' : undefined}
                  aria-sort={column.sortValue ? ariaSort : undefined}
                >
                  {column.sortValue ? (
                    <button
                      type="button"
                      className="report-sort"
                      onClick={() => toggle(column)}
                      data-sort={column.key}
                      title={t('inventory.reports.table.sortBy', { column: column.label })}
                    >
                      {column.label}
                      {active &&
                        (sort.direction === 'asc' ? (
                          <ArrowUp size={12} />
                        ) : (
                          <ArrowDown size={12} />
                        ))}
                    </button>
                  ) : (
                    column.label
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={rowKey(row)}>
              {columns.map((column) => {
                const extra = column.cellClass ? column.cellClass(row) : '';
                const className =
                  [column.align === 'right' ? 'is-num' : '', extra].filter(Boolean).join(' ') ||
                  undefined;
                return (
                  <td key={column.key} className={className}>
                    {column.render(row)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Contenido del tooltip de Recharts con los colores del tema (vía CSS).
 * `rows(payload, label)` devuelve `[{ key, label, value, color? }]`.
 * @param {Object} props - Props de Recharts + `title` y `rows`
 * @returns {JSX.Element|null} Tooltip
 */
export function ChartTooltip({ active, payload, label, title, rows }) {
  if (!active || !payload || !payload.length) return null;
  const lines = rows(payload, label);
  return (
    <div className="report-tooltip">
      <div className="report-tooltip-title">{title ? title(payload, label) : label}</div>
      {lines.map((line) => (
        <div key={line.key} className="report-tooltip-row">
          {line.color && (
            <span
              className="report-tooltip-swatch"
              style={{ background: line.color }}
              aria-hidden="true"
            />
          )}
          <span className="report-tooltip-label">{line.label}</span>
          <span className="report-tooltip-value">{line.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Leyenda propia (siempre con ≥ 2 series): muestra y texto en tinta de texto, no del color.
 * @param {{items: Array<{key: string, label: string, color: string, shape?: string}>}} props - Series
 * @returns {JSX.Element} Leyenda
 */
export function ChartLegend({ items }) {
  return (
    <ul className="report-legend">
      {items.map((item) => (
        <li key={item.key}>
          <span
            className={`report-legend-swatch${item.shape === 'line' ? ' is-line' : ''}`}
            style={{ background: item.color }}
            aria-hidden="true"
          />
          {item.label}
        </li>
      ))}
    </ul>
  );
}

/**
 * Props comunes de ejes y grilla (recesivos, líneas finas, sin punteado).
 * @param {Object} colors - Colores del tema
 * @returns {Object} `{ tick, axisLine, grid }`
 */
export const axisProps = (colors) => ({
  tick: { fill: colors.axis, fontSize: 12 },
  axisLine: { stroke: colors.grid },
  tickLine: false,
});
